<!-- 商品中心 - 商品审核工作台 -->
<template>
  <ContentWrap>
    <el-form
      ref="queryFormRef"
      :inline="true"
      :model="queryParams"
      class="-mb-15px"
      label-width="68px"
    >
      <el-form-item label="商品名称" prop="name">
        <el-input
          v-model="queryParams.name"
          class="!w-240px"
          clearable
          placeholder="请输入商品名称"
          @keyup.enter="handleQuery"
        />
      </el-form-item>
      <el-form-item label="商品分类" prop="categoryId">
        <el-cascader
          v-model="queryParams.categoryId"
          :options="categoryList"
          :props="defaultProps"
          class="!w-240px"
          clearable
          filterable
          placeholder="请选择商品分类"
        />
      </el-form-item>
      <el-form-item label="所属店铺" prop="shopId">
        <el-select
          v-model="queryParams.shopId"
          class="!w-240px"
          clearable
          filterable
          placeholder="请选择店铺"
        >
          <el-option
            v-for="item in shopFilterOptions"
            :key="item.id"
            :label="item.name"
            :value="item.id"
          />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button @click="handleQuery">
          <Icon class="mr-5px" icon="ep:search" />
          搜索
        </el-button>
        <el-button @click="resetQuery">
          <Icon class="mr-5px" icon="ep:refresh" />
          重置
        </el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <ContentWrap>
    <el-tabs v-model="queryParams.auditStatus" @tab-change="handleTabChange">
      <el-tab-pane
        v-for="tab in tabs"
        :key="tab.status"
        :label="tab.name"
        :name="tab.status"
      />
    </el-tabs>
    <el-table v-loading="loading" :data="list" stripe>
      <el-table-column label="商品信息" min-width="280">
        <template #default="{ row }">
          <div class="flex items-center">
            <el-image
              fit="cover"
              :src="row.picUrl"
              class="flex-none w-50px h-50px rounded"
            />
            <div class="ml-10px min-w-0">
              <div class="truncate font-medium">{{ row.name }}</div>
              <div class="text-12px text-[var(--el-text-color-secondary)]">ID: {{ row.id }}</div>
            </div>
          </div>
        </template>
      </el-table-column>
      <el-table-column label="所属店铺" min-width="140" show-overflow-tooltip>
        <template #default="{ row }">{{ shopName(row.shopId) }}</template>
      </el-table-column>
      <el-table-column align="center" label="价格" width="110">
        <template #default="{ row }">¥ {{ fenToYuan(row.price) }}</template>
      </el-table-column>
      <el-table-column align="center" label="库存" width="80" prop="stock" />
      <el-table-column
        :formatter="dateFormatter"
        align="center"
        label="提交时间"
        prop="createTime"
        width="180"
      />
      <el-table-column
        v-if="queryParams.auditStatus !== 1"
        label="审核备注"
        min-width="160"
        prop="auditRemark"
        show-overflow-tooltip
      >
        <template #default="{ row }">
          <span v-if="row.auditRemark">{{ row.auditRemark }}</span>
          <span v-else class="text-[var(--el-text-color-secondary)]">-</span>
        </template>
      </el-table-column>
      <el-table-column
        v-if="queryParams.auditStatus !== 1"
        :formatter="dateFormatter"
        align="center"
        label="审核时间"
        prop="auditTime"
        width="180"
      />
      <el-table-column align="center" fixed="right" label="操作" width="160">
        <template #default="{ row }">
          <el-button link type="primary" @click="openDetail(row.id)">详情</el-button>
          <el-button
            v-if="row.auditStatus === 1"
            v-hasPermi="['product:spu:audit']"
            link
            type="warning"
            @click="handleAudit(row)"
          >
            审核
          </el-button>
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      v-model:limit="queryParams.pageSize"
      v-model:page="queryParams.pageNo"
      :total="total"
      @pagination="getList"
    />
  </ContentWrap>

  <AuditDialog ref="auditDialogRef" @success="getList" />
</template>

<script lang="ts" setup>
import { dateFormatter } from '@/utils/formatTime'
import { defaultProps, handleTree } from '@/utils/tree'
import { fenToYuan } from '@/utils'
import * as ProductSpuApi from '@/api/mall/product/spu'
import * as ProductCategoryApi from '@/api/mall/product/category'
import { getShopSimpleList } from '@/api/merchant/shop-manager'
import { AuditDialog } from '../components'
import { useShopName } from '@/hooks/web/useShopName'
import { useMallStandaloneNav } from '@/hooks/web/useMallStandaloneNav'
import {
  ensureDefaultSelfOperatedShops,
  pickDefaultSelfOperatedShopId
} from '@/utils/mallSelfOperatedShop'
import {
  readMallUiScope,
  type MallUiScope
} from '@/layout/standalone/mallMenuScopes'
import { inject, type Ref } from 'vue'

defineOptions({ name: 'ProductSpuAudit' })

const { shopName } = useShopName()
const { pushMall } = useMallStandaloneNav()

const tabs = [
  { name: '待审核', status: 1 },
  { name: '已通过', status: 2 },
  { name: '已驳回', status: 3 }
]

const loading = ref(false)
const total = ref(0)
const list = ref<ProductSpuApi.Spu[]>([])
const categoryList = ref<any[]>([])
const shopFilterOptions = ref<{ id: number; name: string }[]>([])
const queryFormRef = ref()
const auditDialogRef = ref()

const queryParams = reactive({
  pageNo: 1,
  pageSize: 10,
  name: '',
  categoryId: undefined as number | undefined,
  shopId: undefined as number | undefined,
  /** 不传 tabType，避免按出售中/仓库等库存态过滤 */
  auditStatus: 1 as number
})

const injectedMallScope = inject<Ref<MallUiScope> | undefined>('mallUiScope', undefined)
const resolveSelfScope = () =>
  (injectedMallScope?.value ?? readMallUiScope()) === 'self'

const refreshShopFilterOptions = async () => {
  const selfScope = resolveSelfScope()
  if (selfScope) {
    const selfShops = await ensureDefaultSelfOperatedShops()
    shopFilterOptions.value = selfShops.map((s: any) => ({ id: s.id, name: s.name }))
    if (!queryParams.shopId) {
      const def = pickDefaultSelfOperatedShopId(selfShops)
      if (def) queryParams.shopId = def
    }
  } else {
    const shops = await getShopSimpleList()
    shopFilterOptions.value = (shops || []).map((s: any) => ({ id: s.id, name: s.name }))
  }
}

const getList = async () => {
  loading.value = true
  try {
    const data = await ProductSpuApi.getSpuPage({ ...queryParams })
    list.value = data.list
    total.value = data.total
  } finally {
    loading.value = false
  }
}

const handleQuery = () => {
  queryParams.pageNo = 1
  getList()
}

const resetQuery = () => {
  queryFormRef.value?.resetFields()
  queryParams.auditStatus = 1
  handleQuery()
}

const handleTabChange = (name: string | number) => {
  queryParams.auditStatus = Number(name)
  queryParams.pageNo = 1
  getList()
}

const openDetail = (id: number) => {
  pushMall(`/product/spu/detail/${id}`)
}

const handleAudit = (row: ProductSpuApi.Spu) => {
  auditDialogRef.value?.open(row)
}

onMounted(async () => {
  const data = await ProductCategoryApi.getCategoryList({})
  categoryList.value = handleTree(data, 'id')
  await refreshShopFilterOptions()
  await getList()
})
</script>
